/**
 * Android / desktop Chrome "Install app" prompt. The browser fires
 * `beforeinstallprompt` once, early, and only lets the page show the prompt
 * later from a click - so main.tsx starts listening at boot and the Settings
 * card offers an "Install app" button while one is held. iPhone never fires
 * it (install is Share -> Add to Home Screen, explained in the card).
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = (): void => {
  for (const l of listeners) l();
};

export const listenForInstallPrompt = (): void => {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser's mini-infobar from appearing on its own.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
};

export const canPromptInstall = (): boolean => deferred !== null;

export const onInstallPromptChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Show the install prompt. Must be called from a click. */
export const promptInstall = async (): Promise<boolean> => {
  const event = deferred;
  if (!event) return false;
  // A prompt can be shown once; the browser fires a fresh event if allowed again.
  deferred = null;
  notify();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
};
